<script setup lang="ts">
import { Link, usePage } from '@inertiajs/vue3';
import {
    Activity,
    Bot,
    CreditCard,
    FileText,
    FolderGit2,
    Gauge,
    Inbox,
    KeyRound,
    LayoutDashboard,
    LayoutGrid,
    MessageSquare,
    Phone,
    Receipt,
    Send,
    Settings,
    Shield,
    Tag,
    Users,
    Wallet as WalletIcon,
    Webhook,
} from 'lucide-vue-next';
import { computed } from 'vue';
import AppLogo from '@/components/AppLogo.vue';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import NavFooter from '@/components/NavFooter.vue';
import NavMain from '@/components/NavMain.vue';
import NavUser from '@/components/NavUser.vue';
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useI18n } from '@/composables/useI18n';
import { dashboard } from '@/routes';
import * as apiApplicationsRoute from '@/routes/admin/api-applications';
import * as couponsRoute from '@/routes/admin/coupons';
import * as incomingMessagesRoute from '@/routes/admin/incoming-messages';
import * as invoicesRoute from '@/routes/admin/invoices';
import * as messageCampaignsRoute from '@/routes/admin/message-campaigns';
import * as messagesRoute from '@/routes/admin/messages';
import * as plansRoute from '@/routes/admin/plans';
import * as promotionsRoute from '@/routes/admin/promotions';
import * as rolesRoute from '@/routes/admin/roles';
import * as settingsRoute from '@/routes/admin/settings';
import * as subscriptionsRoute from '@/routes/admin/subscriptions';
import * as webhooksRoute from '@/routes/admin/webhooks';
import * as whatsappInstancesRoute from '@/routes/admin/whatsapp-instances';
import * as adminPanelDashboard from '@/routes/admin-panel';
import * as adminPanelApiApplicationsRoute from '@/routes/admin-panel/api-applications';
import * as adminPanelContactGroupsRoute from '@/routes/admin-panel/contact-groups';
import * as adminPanelContactsRoute from '@/routes/admin-panel/contacts';
import * as adminPanelIncomingMessagesRoute from '@/routes/admin-panel/incoming-messages';
import * as adminPanelInstanceIncidentsRoute from '@/routes/admin-panel/instance-incidents';
import * as adminPanelMessageCampaignsRoute from '@/routes/admin-panel/message-campaigns';
import * as adminPanelMessageTemplatesRoute from '@/routes/admin-panel/message-templates';
import * as adminPanelMessagesRoute from '@/routes/admin-panel/messages';
import * as adminPanelPaymentEventsRoute from '@/routes/admin-panel/payment-events';
import * as adminPanelUsersRoute from '@/routes/admin-panel/users';
import * as adminPanelWebhookDeliveriesRoute from '@/routes/admin-panel/webhook-deliveries';
import * as adminPanelWhatsappInstancesRoute from '@/routes/admin-panel/whatsapp-instances';
import * as userSubscriptionsRoute from '@/routes/subscriptions';
import * as walletRoute from '@/routes/wallet';
import type { NavItem } from '@/types';

type ExtendedNavItem = NavItem & { requiresPermission?: string };

const page = usePage();
const auth = computed(() => page.props.auth);
const { __ } = useI18n();

const hasPermission = (perm?: string) => {
    if (!perm) {
        return true;
    }

    const user = auth.value?.user;

    if (!user || !user.permissions) {
        return false;
    }

    return (user.permissions as string[]).includes(perm);
};

const isAdmin = computed(() => {
    const roles = auth.value?.user?.roles as string[] | undefined;

    return roles?.includes('super-admin') || roles?.includes('admin');
});

const workspaceNavItems = computed<ExtendedNavItem[]>(() => [
    {
        title: __('nav.dashboard'),
        href: dashboard(),
        icon: LayoutGrid,
    },
    {
        title: __('nav.whatsapp'),
        href: whatsappInstancesRoute.index().url,
        icon: MessageSquare,
        requiresPermission: 'manage whatsapp instances',
    },
    {
        title: __('nav.contacts'),
        href: '/admin/contacts',
        icon: Phone,
        requiresPermission: 'manage contacts',
    },
    {
        title: __('nav.contact_groups'),
        href: '/admin/contact-groups',
        icon: Users,
        requiresPermission: 'manage contacts',
    },
    {
        title: __('nav.message_templates'),
        href: '/admin/message-templates',
        icon: FileText,
        requiresPermission: 'manage message templates',
    },
    {
        title: __('nav.messages'),
        href: messagesRoute.index().url,
        icon: Send,
        requiresPermission: 'send messages',
    },
    {
        title: __('nav.message_campaigns'),
        href: messageCampaignsRoute.index().url,
        icon: MessageSquare,
        requiresPermission: 'manage message campaigns',
    },
    {
        title: __('nav.incoming_messages'),
        href: incomingMessagesRoute.index().url,
        icon: Inbox,
        requiresPermission: 'view incoming messages',
    },
    {
        title: __('nav.api_applications'),
        href: apiApplicationsRoute.index().url,
        icon: KeyRound,
        requiresPermission: 'manage api applications',
    },
    {
        title: __('nav.chatbot'),
        href: '/admin/chatbots',
        icon: Bot,
        requiresPermission: 'manage chatbot',
    },
    {
        title: __('nav.webhooks'),
        href: webhooksRoute.index().url,
        icon: Webhook,
        requiresPermission: 'manage webhooks',
    },
]);

const adminNavItems = computed<ExtendedNavItem[]>(() => [
    {
        title: __('nav.overview'),
        href: adminPanelDashboard.dashboard().url,
        icon: LayoutDashboard,
    },
    {
        title: __('nav.users'),
        href: adminPanelUsersRoute.index().url,
        icon: Users,
        requiresPermission: 'manage users',
    },
    {
        title: __('nav.roles'),
        href: rolesRoute.index().url,
        icon: Shield,
        requiresPermission: 'manage roles',
    },
    {
        title: __('nav.whatsapp_instances'),
        href: adminPanelWhatsappInstancesRoute.index().url,
        icon: MessageSquare,
        requiresPermission: 'manage all whatsapp instances',
    },
    {
        title: __('nav.instance_incidents'),
        href: adminPanelInstanceIncidentsRoute.index().url,
        icon: Activity,
        requiresPermission: 'manage all whatsapp instances',
    },
    {
        title: __('nav.contacts'),
        href: adminPanelContactsRoute.index().url,
        icon: Phone,
        requiresPermission: 'manage all contacts',
    },
    {
        title: __('nav.contact_groups'),
        href: adminPanelContactGroupsRoute.index().url,
        icon: Users,
        requiresPermission: 'manage all contacts',
    },
    {
        title: __('nav.message_templates'),
        href: adminPanelMessageTemplatesRoute.index().url,
        icon: FileText,
        requiresPermission: 'manage all message templates',
    },
    {
        title: __('nav.messages'),
        href: adminPanelMessagesRoute.index().url,
        icon: Send,
        requiresPermission: 'manage all messages',
    },
    {
        title: __('nav.campaigns'),
        href: adminPanelMessageCampaignsRoute.index().url,
        icon: MessageSquare,
        requiresPermission: 'manage all message campaigns',
    },
    {
        title: __('nav.incoming_messages'),
        href: adminPanelIncomingMessagesRoute.index().url,
        icon: Inbox,
        requiresPermission: 'manage all incoming messages',
    },
    {
        title: __('nav.api_applications'),
        href: adminPanelApiApplicationsRoute.index().url,
        icon: KeyRound,
        requiresPermission: 'manage all api applications',
    },
    {
        title: __('nav.plans'),
        href: plansRoute.index().url,
        icon: CreditCard,
        requiresPermission: 'manage plans',
    },
    {
        title: __('nav.promo_codes'),
        href: couponsRoute.index().url,
        icon: Tag,
        requiresPermission: 'manage coupons',
    },
    {
        title: __('nav.promotions'),
        href: promotionsRoute.index().url,
        icon: Tag,
        requiresPermission: 'manage promotions',
    },
    {
        title: __('nav.subscriptions'),
        href: subscriptionsRoute.index().url,
        icon: Receipt,
        requiresPermission: 'manage subscriptions',
    },
    {
        title: __('nav.invoices'),
        href: invoicesRoute.index().url,
        icon: FileText,
        requiresPermission: 'manage invoices',
    },
    {
        title: __('nav.payment_events'),
        href: adminPanelPaymentEventsRoute.index().url,
        icon: CreditCard,
        requiresPermission: 'manage subscriptions',
    },
    {
        title: __('nav.webhook_deliveries'),
        href: adminPanelWebhookDeliveriesRoute.index().url,
        icon: Webhook,
        requiresPermission: 'manage webhooks',
    },
    {
        title: __('nav.settings'),
        href: settingsRoute.edit().url,
        icon: Settings,
        requiresPermission: 'manage settings',
    },
]);

const mainNavItems = computed(() =>
    workspaceNavItems.value.filter((item) =>
        hasPermission(item.requiresPermission),
    ),
);

const billingNavItems = computed<NavItem[]>(() => [
    {
        title: __('nav.subscriptions'),
        href: userSubscriptionsRoute.manage().url,
        icon: CreditCard,
    },
    {
        title: __('nav.usage'),
        href: userSubscriptionsRoute.usage().url,
        icon: Gauge,
    },
    {
        title: __('nav.invoices'),
        href: userSubscriptionsRoute.invoices().url,
        icon: Receipt,
    },
    {
        title: __('nav.wallet'),
        href: walletRoute.index().url,
        icon: WalletIcon,
    },
]);

const adminItems = computed(() =>
    adminNavItems.value.filter((item) =>
        hasPermission(item.requiresPermission),
    ),
);

const footerNavItems = computed<NavItem[]>(() => [
    {
        title: __('common.repository'),
        href: 'https://github.com/laravel/vue-starter-kit',
        icon: FolderGit2,
    },
]);
</script>

<template>
    <Sidebar collapsible="icon" variant="inset">
        <SidebarHeader>
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton size="lg" as-child>
                        <Link :href="dashboard()">
                            <AppLogo />
                        </Link>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>
        </SidebarHeader>

        <SidebarContent>
            <NavMain :title="__('nav.my_space')" :items="mainNavItems" />
            <NavMain
                v-if="isAdmin && adminItems.length > 0"
                :title="__('nav.administration')"
                :items="adminItems"
            />
            <NavMain :title="__('nav.billing')" :items="billingNavItems" />
        </SidebarContent>

        <SidebarFooter>
            <NavFooter :items="footerNavItems" />
            <div class="px-2 pb-1">
                <LanguageSwitcher />
            </div>
            <NavUser />
        </SidebarFooter>
    </Sidebar>
    <slot />
</template>
